import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams, Navigate } from 'react-router-dom';
import { homeStateActions } from '../../_actions';
import {
  Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, IconButton, InputAdornment,
  MenuItem, Pagination, Popover, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, useMediaQuery, Tooltip,
} from '@mui/material';
import {
  AddOutlined,
  ApartmentOutlined,
  ArrowBack,
  AssignmentOutlined,
  CalendarTodayOutlined,
  CheckCircle,
  CheckCircleOutline,
  ChevronLeft,
  ChevronRight,
  CloudUploadOutlined,
  CloseOutlined,
  DescriptionOutlined,
  InsertDriveFileOutlined,
  KeyboardArrowDownOutlined,
  PersonOutline,
  RefreshOutlined,
  ReportProblemOutlined,
  SearchOffOutlined,
  SearchOutlined,
  VisibilityOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getArStatusDataService,
  getAppointmentBillInfoService,
  updateArStatusService,
  getArHospitalListService,
} from './arReconciliation.service';
import { centralGetSignedUrlService } from '../../_services';
import { centralUploadFilesService } from '../../_services/centralServices/centralUploadFiles.service';
import {
  hasArAccess,
  canPerformArAction,
  getUserRestrictedHospitals,
} from './arAccess';
import './ARReconciliation.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const inr = (n) => {
  if (n === null || n === undefined || n === '' || isNaN(Number(n))) return '--';
  const num = Math.round(Number(n));
  if (num < 0) {
    return `-₹${Math.abs(num).toLocaleString('en-IN')}`;
  }
  return `₹${num.toLocaleString('en-IN')}`;
};

// ─── IST Date Conversion & Formatting Helpers ─────────────────────────────────
// Ensures all date display and filtering operates strictly in Indian Standard Time (Asia/Kolkata, UTC+05:30),
// completely independent of server or browser local timezones.

const parseToDate = (val) => {
  if (!val && val !== 0) return null;
  if (val instanceof Date) {
    return isNaN(val.getTime()) ? null : val;
  }
  if (typeof val === 'number') {
    // Check if unix seconds (10 digits) vs milliseconds (13 digits)
    const ms = val < 1e11 ? val * 1000 : val;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!trimmed || trimmed === '--' || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') {
      return null;
    }

    // Pure 'YYYY-MM-DD' - create date at 12:00 PM IST (06:30 UTC) so it anchors cleanly to that exact calendar date in IST
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const parts = trimmed.split('-').map(Number);
      return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], 6, 30, 0));
    }

    // 'DD-MM-YYYY' or 'DD/MM/YYYY'
    const ddmmyyyy = trimmed.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
    if (ddmmyyyy) {
      const day = Number(ddmmyyyy[1]);
      const m = Number(ddmmyyyy[2]);
      const y = Number(ddmmyyyy[3]);
      return new Date(Date.UTC(y, m - 1, day, 6, 30, 0));
    }

    // SQL datetime 'YYYY-MM-DD HH:mm:ss' without timezone offset (standard IST timestamp from Indian backends)
    if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(trimmed)) {
      const isoWithOffset = trimmed.replace(' ', 'T') + '+05:30';
      const d = new Date(isoWithOffset);
      if (!isNaN(d.getTime())) return d;
    }

    // Standard ISO string (e.g. '2026-10-01T11:12:00.000Z' or '+05:30')
    const d = new Date(trimmed);
    if (!isNaN(d.getTime())) return d;
  }

  try {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  } catch (e) {
    return null;
  }
};

/**
 * Formats any date input (ISO UTC, IST string, timestamp, etc.) to IST date string (e.g. "01 Oct 2026").
 * Always formats in 'Asia/Kolkata' timezone to ensure identical output regardless of client/server timezone.
 */
const fmtDate = (val, options = {}) => {
  if (!val && val !== 0) return '--';
  const d = parseToDate(val);
  if (!d) return typeof val === 'string' && val.trim() ? val : '--';
  try {
    if (options.includeTime) {
      return d.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }
    return d.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch (e) {
    return typeof val === 'string' ? val : '--';
  }
};

/**
 * Converts any date input to 'YYYY-MM-DD' in 'Asia/Kolkata' (IST) timezone.
 */
const toIstYmd = (val) => {
  if (!val && val !== 0) return '';
  const d = parseToDate(val);
  if (!d) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(d);

    let year = '';
    let month = '';
    let day = '';
    for (let i = 0; i < parts.length; i++) {
      if (parts[i].type === 'year') year = parts[i].value;
      if (parts[i].type === 'month') month = parts[i].value;
      if (parts[i].type === 'day') day = parts[i].value;
    }
    return (year && month && day) ? `${year}-${month}-${day}` : '';
  } catch (e) {
    return '';
  }
};

/**
 * Extracts YYYY-MM-DD in IST timezone from case date to match table display and date pickers
 */
const getCaseDateYmd = (c) => {
  if (!c) return '';
  const val = c.dateOfAdmission || c.apd || c.admission_date || c.date_of_admission || c.doa || c.admissionDate;
  return toIstYmd(val);
};

/**
 * Finds the last action item in actionTrail matching a given action name.
 * e.g. getLastActionItem(c, 'hospital_approve') or getLastActionItem(c, 'hospital_dispute')
 */
const getLastActionItem = (caseObj, targetAction) => {
  const trail = caseObj?.actionTrail || caseObj?.hospitalActionTrail || caseObj?.action_trail || [];
  if (!Array.isArray(trail) || trail.length === 0) return null;
  const target = (targetAction || '').toLowerCase();
  for (let i = trail.length - 1; i >= 0; i--) {
    const item = trail[i];
    const act = (item?.action || '').toLowerCase();
    if (act === target) {
      return item;
    }
  }
  return null;
};

// Status pill derived from API status, actionTrail, or tab context.
const arStatusInfo = (c, defaultTab = '') => {
  if (!c) return { label: 'Pending', cls: 'pending' };

  const status = (c.status || c.hospitalStatus || c.hospital_status || c.actionStatus || '').toString().trim().toLowerCase();

  // Table 2 — hospital signed off
  if (status === 'hospital_approve') {
    return { label: 'Signed Off', cls: 'signed' };
  }

  // Table 3 — hospital raised a dispute
  if (status === 'hospital_dispute') {
    return { label: 'Disputed', cls: 'disputed' };
  }

  // Table 1 — finance_approve (open cases awaiting hospital action)
  if (status === 'finance_approve') {
    // If this case was disputed by the hospital earlier and GHV has since
    // re-approved it, surface that explicitly. Otherwise it is a fresh pending case.
    const trail = c.actionTrail || c.hospitalActionTrail || c.action_trail || [];
    const wasDisputedByHospital =
      Array.isArray(trail) &&
      trail.some((t) => (t?.action || '').toString().trim().toLowerCase() === 'hospital_dispute');

    if (wasDisputedByHospital) {
      // Same open/pending behaviour (AR/AP input + Sign Off / Dispute actions) and
      // same colour as Pending — only the label differs.
      return { label: 'Dispute Updated by GHV', cls: 'pending' };
    }
    return { label: 'Pending', cls: 'pending' };
  }

  // Fallback based on tab context (keeps lists sane if status key is missing)
  if (defaultTab === 'signed_off') return { label: 'Signed Off', cls: 'signed' };
  if (defaultTab === 'dispute') return { label: 'Disputed', cls: 'disputed' };

  return { label: 'Pending', cls: 'pending' };
};

/**
 * Opens a document in a new tab using a freshly-fetched signed URL.
 * Pre-opens the tab synchronously (on the click) so the popup blocker doesn't block it.
 * Same behaviour as the DocsModal "View" action.
 */
const openSignedUrl = async (docUrl) => {
  if (!docUrl) return;
  let newTab = null;
  try {
    newTab = window.open('about:blank', '_blank');
  } catch (e) {
    console.warn('Popup window blocked or error:', e);
  }
  try {
    const res = await centralGetSignedUrlService({ urls: [docUrl] });
    const signedUrl =
      res?.result?.[0] ||
      (Array.isArray(res?.result) ? res.result[0] : null) ||
      (Array.isArray(res) ? res[0] : null) ||
      docUrl;

    if (signedUrl) {
      if (newTab && !newTab.closed) newTab.location.href = signedUrl;
      else window.open(signedUrl, '_blank');
    } else {
      if (newTab && !newTab.closed) newTab.close();
      toast.error('Unable to fetch signed document URL');
    }
  } catch (err) {
    if (newTab && !newTab.closed) newTab.close();
    console.error('getSignedUrl error:', err);
    toast.error('Failed to open document');
  }
};

const PAGE_SIZE = 20;

const DISPUTE_BUCKETS = [
  'Lens Charges',
  'Consumables',
  'Settlement',
  'Room Rent',
  'Medicine',
  'Implant',
  'Package',
  'Agreement',
  'Diagnostics',
  'OT Charges',
  'Payment Difference',
  'Other',
];

const thSx = {
  fontWeight: 700,
  fontSize: '11px',
  color: '#7a84a3',
  whiteSpace: 'nowrap',
  background: '#f8fafc',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  py: 1.5,
  px: 2,
};

const stickyAppThSx = {
  ...thSx,
  position: 'sticky',
  left: 0,
  zIndex: 10,
  width: 150,
  minWidth: 150,
  maxWidth: 150,
  boxSizing: 'border-box',
  background: '#f8fafc',
  borderRight: '1px solid #edf0f7',
  whiteSpace: 'nowrap',
};

const stickyPtThSx = {
  ...thSx,
  position: 'sticky',
  left: 150,
  zIndex: 10,
  width: 185,
  minWidth: 185,
  maxWidth: 185,
  boxSizing: 'border-box',
  background: '#f8fafc',
  borderRight: '2px solid #dde3ef',
  boxShadow: '4px 0 8px -2px rgba(13, 31, 53, 0.12)',
  whiteSpace: 'nowrap',
};

const stickyAppTdSx = {
  position: 'sticky',
  left: 0,
  zIndex: 5,
  width: 150,
  minWidth: 150,
  maxWidth: 150,
  boxSizing: 'border-box',
  borderRight: '1px solid #edf0f7',
  whiteSpace: 'nowrap',
};

const stickyPtTdSx = {
  position: 'sticky',
  left: 150,
  zIndex: 5,
  width: 185,
  minWidth: 185,
  maxWidth: 185,
  boxSizing: 'border-box',
  borderRight: '2px solid #dde3ef',
  boxShadow: '4px 0 8px -2px rgba(13, 31, 53, 0.12)',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  lineHeight: 1.35,
};

const thStickySx = {
  ...thSx,
  position: 'sticky',
  right: 0,
  zIndex: 10,
  background: '#f8fafc',
  borderLeft: '1.5px solid #dde3ef',
  boxShadow: '-4px 0 8px -2px rgba(13, 31, 53, 0.08)',
};

const tdStickySx = {
  position: 'sticky',
  right: 0,
  zIndex: 4,
  background: '#ffffff',
  borderLeft: '1.5px solid #dde3ef',
  boxShadow: '-4px 0 8px -2px rgba(13, 31, 53, 0.08)',
  py: 1.5,
  px: 2,
};

// ─── Left Sidebar ──────────────────────────────────────────────────────────────
const Sidebar = ({ tab, setTab, isCollapsed, setIsCollapsed }) => {
  const navItems = [
    {
      key: 'dash',
      label: 'AR Reconciliation',
      icon: <AssignmentOutlined className="ar-nav-icon" />,
    },
    {
      key: 'signed',
      label: 'Signed Off Cases',
      icon: <CheckCircleOutline className="ar-nav-icon" />,
    },
    {
      key: 'disp',
      label: 'Raised Disputes',
      icon: <ReportProblemOutlined className="ar-nav-icon" />,
    },
  ];

  return (
    <aside className={`ar-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="ar-sidebar-header">
        <div className="ar-sidebar-header-left">
          <div className="ar-sidebar-header-icon">
            <AssignmentOutlined sx={{ fontSize: 18, color: '#fff' }} />
          </div>
          <div className="ar-sidebar-header-text">
            <div className="ar-sidebar-title">AR Reconciliation</div>
            <div className="ar-sidebar-subtitle">Hospital Portal</div>
          </div>
        </div>
        <Tooltip
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          placement={isCollapsed ? 'right' : 'bottom'}
          arrow
        >
          <IconButton
            size="small"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="ar-sidebar-toggle-btn"
            sx={{
              color: '#8ea6b8',
              p: '6px',
              borderRadius: '8px',
              background: isCollapsed ? 'rgba(255,255,255,0.08)' : 'transparent',
              transition: 'all 0.2s ease',
              flexShrink: 0,
              '&:hover': {
                color: '#ffffff',
                background: 'rgba(255,255,255,0.15)',
              },
            }}
          >
            {isCollapsed ? (
              <ChevronRight sx={{ fontSize: 20 }} />
            ) : (
              <ChevronLeft sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Tooltip>
      </div>

      <div className="ar-nav-section">
        <div className="ar-nav-section-label">Navigation</div>
        {navItems.map((item) => {
          const itemEl = (
            <div
              key={item.key}
              className={`ar-nav-item${tab === item.key ? ' active' : ''}`}
              onClick={() => setTab(item.key)}
            >
              {item.icon}
              <span className="ar-nav-label">{item.label}</span>
            </div>
          );

          return isCollapsed ? (
            <Tooltip key={item.key} title={item.label} placement="right" arrow>
              {itemEl}
            </Tooltip>
          ) : (
            itemEl
          );
        })}
      </div>
    </aside>
  );
};

// ─── Stats Row ─────────────────────────────────────────────────────────────────
const StatsRow = ({ openCount, signedCount, disputeCount, onTabSelect }) => {
  const stats = [
    {
      key: 'dash',
      label: 'Open Cases',
      value: openCount,
      cls: 'open',
      icon: <AssignmentOutlined sx={{ fontSize: 20, color: '#3D7CC9' }} />,
      iconBg: 'open',
    },
    {
      key: 'signed',
      label: 'Signed Off',
      value: signedCount,
      cls: 'signed',
      icon: <CheckCircleOutline sx={{ fontSize: 20, color: '#137a3f' }} />,
      iconBg: 'signed',
    },
    {
      key: 'disp',
      label: 'Raised Disputes',
      value: disputeCount,
      cls: 'disputed',
      icon: <ReportProblemOutlined sx={{ fontSize: 20, color: '#b75c00' }} />,
      iconBg: 'disputed',
    },
  ];

  return (
    <div className="ar-stats-row">
      {stats.map((s) => (
        <div
          key={s.label}
          className="ar-stat-card"
          style={{ cursor: onTabSelect ? 'pointer' : 'default' }}
          onClick={() => onTabSelect && onTabSelect(s.key)}
        >
          <div className={`ar-stat-icon ${s.iconBg}`}>{s.icon}</div>
          <div className="ar-stat-info">
            <div className={`ar-stat-value ${s.cls}`}>{s.value}</div>
            <div className="ar-stat-label">{s.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Filter Bar Component ──────────────────────────────────────────────────────
const ARFilterBar = ({
  title = 'Filter Cases',
  filters,
  onChange,
  onReset,
}) => {
  const hasActiveFilters = Boolean(
    (filters?.app || '').trim() ||
    (filters?.pt || '').trim() ||
    filters?.from ||
    filters?.to
  );

  const inputSx = {
    flex: 1,
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      height: '40px',
      borderRadius: '10px',
      background: '#ffffff',
      fontSize: '13px',
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: '#dde3ef',
        transition: 'border-color 0.2s ease',
      },
      '&:hover fieldset': {
        borderColor: '#3D7CC9',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#3D7CC9',
        borderWidth: '1.5px',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '13px',
      color: '#7a84a3',
      background: '#ffffff',
      px: '4px',
      '&.Mui-focused': {
        color: '#3D7CC9',
      },
    },
  };

  return (
    <div className="ar-filter-card">
      <div className="ar-filter-title">
        <SearchOutlined sx={{ fontSize: 14, color: '#7a84a3' }} />
        {title}
      </div>
      <div className="ar-filter-row">
        <TextField
          label="App ID"
          placeholder="e.g. bopYPbEp25"
          size="small"
          value={filters.app}
          onChange={onChange('app')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined sx={{ fontSize: 16, color: '#7a84a3' }} />
              </InputAdornment>
            ),
          }}
          sx={inputSx}
        />
        <TextField
          label="Patient Name"
          placeholder="Search patient"
          size="small"
          value={filters.pt}
          onChange={onChange('pt')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonOutline sx={{ fontSize: 16, color: '#7a84a3' }} />
              </InputAdornment>
            ),
          }}
          sx={inputSx}
        />
        <TextField
          label="Admission Date (From)"
          type="date"
          size="small"
          value={filters.from}
          onChange={onChange('from')}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            max: filters.to || undefined,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CalendarTodayOutlined sx={{ fontSize: 14, color: '#7a84a3' }} />
              </InputAdornment>
            ),
          }}
          sx={inputSx}
        />
        <TextField
          label="Admission Date (To)"
          type="date"
          size="small"
          value={filters.to}
          onChange={onChange('to')}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: filters.from || undefined,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CalendarTodayOutlined sx={{ fontSize: 14, color: '#7a84a3' }} />
              </InputAdornment>
            ),
          }}
          sx={inputSx}
        />
        <Button
          variant="outlined"
          onClick={hasActiveFilters ? onReset : undefined}
          sx={{
            height: '40px',
            textTransform: 'none',
            borderRadius: '10px',
            fontSize: '13px',
            fontWeight: 600,
            px: 2.5,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            transition: 'all 0.2s ease',
            background: hasActiveFilters ? '#eef4ff' : '#f8fafc',
            borderColor: hasActiveFilters ? '#3D7CC9' : '#dde3ef',
            color: hasActiveFilters ? '#3D7CC9' : '#94a3b8',
            cursor: hasActiveFilters ? 'pointer' : 'not-allowed',
            opacity: hasActiveFilters ? 1 : 0.7,
            '&:hover': {
              background: hasActiveFilters ? '#3D7CC9' : '#f8fafc',
              borderColor: hasActiveFilters ? '#3D7CC9' : '#dde3ef',
              color: hasActiveFilters ? '#ffffff' : '#94a3b8',
            },
          }}
        >
          Reset Filters
        </Button>
      </div>
    </div>
  );
};

// ─── Supporting Documents Modal ───────────────────────────────────────────────
const DocsModal = ({ open, onClose, attachments = [], appId, patientName }) => {
  const isMobile = useMediaQuery('(max-width:599px)');
  const [loadingIdx, setLoadingIdx] = useState(null);

  const docList = [];
  if (Array.isArray(attachments) && attachments.length > 0) {
    attachments.forEach((att, attIdx) => {
      const docName = att.key || `Document ${attIdx + 1}`;
      if (Array.isArray(att.urls) && att.urls.length > 0) {
        att.urls.forEach((uObj, uIdx) => {
          docList.push({
            id: `${attIdx}-${uIdx}`,
            title: `${docName}${att.urls.length > 1 ? ` (v${uIdx + 1})` : ''}`,
            url: uObj.url,
            status: uObj.status || 'Available',
            uploader: uObj.uploadedByUsername || '',
            declineReason: uObj.declineReason || '',
            timestamp: uObj.timestamp ? fmtDate(uObj.timestamp, { includeTime: true }) : '',
          });
        });
      } else if (att.url) {
        docList.push({
          id: `${attIdx}`,
          title: docName,
          url: att.url,
          status: 'Available',
        });
      }
    });
  }

  const handleView = async (idx, docUrl) => {
    if (loadingIdx != null) return;
    setLoadingIdx(idx);

    let newTab = null;
    try {
      newTab = window.open('about:blank', '_blank');
    } catch (e) {
      console.warn('Popup window blocked or error:', e);
    }

    try {
      const res = await centralGetSignedUrlService({ urls: [docUrl] });
      const signedUrl =
        res?.result?.[0] ||
        (Array.isArray(res?.result) ? res.result[0] : null) ||
        (Array.isArray(res) ? res[0] : null) ||
        docUrl;

      if (signedUrl) {
        if (newTab && !newTab.closed) {
          newTab.location.href = signedUrl;
        } else {
          window.open(signedUrl, '_blank');
        }
      } else {
        if (newTab && !newTab.closed) newTab.close();
        toast.error('Unable to fetch signed document URL');
      }
    } catch (err) {
      if (newTab && !newTab.closed) newTab.close();
      console.error('getSignedUrl error:', err);
      toast.error('Failed to open document');
    } finally {
      setLoadingIdx(null);
    }
  };

  const handleClose = () => {
    setLoadingIdx(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : '14px', m: isMobile ? 0 : 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: '15px', borderBottom: '1px solid #e3e8f3', pb: 1.5 }}>
        Supporting Documents
        <Typography sx={{ fontSize: '12px', color: '#7a84a3', fontWeight: 400, mt: 0.3 }}>
          {appId} · {patientName}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: isMobile ? '12px' : '16px 24px' }}>
        {docList.length === 0 ? (
          <div className="ar-panel" style={{ margin: '8px 0', border: '1px dashed #dde3ef' }}>
            <div className="ar-empty">No supporting documents uploaded for this case.</div>
          </div>
        ) : (
          docList.map((doc, i) => (
            <div key={doc.id || i} className="ar-doc-row" style={{ alignItems: 'center' }}>
              <div className="ar-doc-row-left">
                <DescriptionOutlined sx={{ color: '#3D7CC9', fontSize: '20px', flexShrink: 0 }} />
                <div>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0d1f35' }}>
                    {doc.title}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#7a84a3' }}>
                    {doc.uploader ? `By: ${doc.uploader}` : ''} {doc.timestamp ? `· ${doc.timestamp}` : ''}
                  </Typography>
                  {doc.declineReason && (
                    <Typography sx={{ fontSize: '11px', color: '#b75c00', mt: 0.3, background: '#fff4e5', p: '2px 6px', borderRadius: '4px' }}>
                      Reason: {doc.declineReason}
                    </Typography>
                  )}
                </div>
                <span className={`ar-pill ${doc.status === 'Approved' ? 'signed' : (doc.status === 'Declined' ? 'disputed' : 'verified')}`}>
                  {doc.status}
                </span>
              </div>
              <Button
                size="small"
                variant="outlined"
                disabled={loadingIdx === i}
                onClick={() => handleView(i, doc.url)}
                startIcon={
                  loadingIdx === i ? (
                    <CircularProgress size={13} color="inherit" />
                  ) : (
                    <VisibilityOutlined sx={{ fontSize: '14px' }} />
                  )
                }
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  flexShrink: 0,
                  minWidth: '85px',
                  borderColor: '#3D7CC9',
                  color: '#3D7CC9',
                  '&:hover': { borderColor: '#2d6bb5', background: '#eef4ff' },
                  '&.Mui-disabled': { borderColor: '#dde3ef', color: '#7a84a3' },
                }}
              >
                {loadingIdx === i ? 'Opening...' : 'View'}
              </Button>
            </div>
          ))
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #e3e8f3', p: '12px 16px' }}>
        <Button onClick={handleClose} sx={{ textTransform: 'none', borderRadius: '8px' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Interactive Action Modal (Sign Off / Raise Dispute) ───────────────────────
const ActionModal = ({
  open,
  onClose,
  caseObj,
  actionType = 'hospital_approve',
  hospitalArap = 0,
  onActionSuccess,
}) => {
  const isMobile = useMediaQuery('(max-width:599px)');
  const fileInputRef = useRef(null);

  const [inputArap, setInputArap] = useState(hospitalArap != null ? String(hospitalArap) : '');
  const [validatedArap, setValidatedArap] = useState(hospitalArap != null ? Number(hospitalArap) : 0);
  const [hasValidated, setHasValidated] = useState(true);

  const [disputeBucket, setDisputeBucket] = useState('');
  const [remarks, setRemarks] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const initialVal = hospitalArap != null ? String(hospitalArap) : '';
      setInputArap(initialVal);
      setValidatedArap(hospitalArap != null ? Number(hospitalArap) : 0);
      setHasValidated(true);
      setDisputeBucket('');
      setRemarks('');
      setUploadedFiles([]);
      setUploading(false);
      setIsDragOver(false);
      setSubmitting(false);
    }
  }, [open, caseObj, hospitalArap]);

  if (!caseObj) return null;

  const appId = caseObj.appId || caseObj.app;
  const patientName = caseObj.patientName || caseObj.pt || '--';
  const hospitalShare = caseObj.hospital_share_total != null ? caseObj.hospital_share_total : (caseObj.hs || 0);
  const financeArap = caseObj.ghv_share_total != null ? caseObj.ghv_share_total : (caseObj.gs || 0);

  const roundedInput = Math.round(Number(validatedArap) || 0);
  const roundedFinance = Math.round(Number(financeArap) || 0);

  const isMatch = hasValidated && roundedInput === roundedFinance;
  const difference = roundedFinance - roundedInput;
  const isMismatch = hasValidated && !isMatch;

  const handleValidate = () => {
    const parsed = Number(inputArap);
    if (inputArap === '' || inputArap === '-' || isNaN(parsed)) {
      toast.error('Please enter a valid amount');
      return;
    }
    setValidatedArap(parsed);
    setHasValidated(true);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    if (val === '' || val === '-' || /^-?\d*\.?\d*$/.test(val)) {
      setInputArap(val);
      if (val === '' || val === '-' || Number(val) !== validatedArap) {
        setHasValidated(false);
      } else {
        setHasValidated(true);
      }
    }
  };

  const uploadFilesList = async (filesList) => {
    const files = Array.from(filesList || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('file', f));
      formData.append('onlyUrl', 'true');
      formData.append('lead_id', appId || 'ar_reconciliation');

      const res = await centralUploadFilesService(formData);
      const data = res?.data || res?.result || [];
      if (Array.isArray(data) && data.length > 0) {
        const newUrls = data.map((item, idx) => ({
          name: item.fileName || files[idx]?.name || `Document ${uploadedFiles.length + idx + 1}`,
          url: item.Url || item.url || item.s3Url || item,
        }));
        setUploadedFiles((prev) => [...prev, ...newUrls]);
      }
    } catch (err) {
      console.error('File upload error:', err);
      toast.error('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e) => {
    uploadFilesList(e.target.files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer && e.dataTransfer.files) {
      uploadFilesList(e.dataTransfer.files);
    }
  };

  const removeFile = (idx) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // Sign Off Submit
  const handleSignOff = async () => {
    setSubmitting(true);
    try {
      await onActionSuccess(caseObj, 'hospital_approve', {
        arap_at_hospital: roundedInput,
      });
      onClose();
    } catch (err) {
      console.error('Sign off error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Dispute Submit
  const handleRaiseDispute = async () => {
    // AR/AP at Hospital must be entered and validated
    if (inputArap === '' || isNaN(Number(inputArap)) || !hasValidated) {
      toast.error('Please enter and validate AR/AP at Hospital');
      return;
    }
    if (!remarks.trim()) {
      toast.error('Remarks are mandatory for raising a dispute');
      return;
    }
    if (!disputeBucket) {
      toast.error('Please select a dispute bucket');
      return;
    }
    setSubmitting(true);
    try {
      await onActionSuccess(caseObj, 'hospital_dispute', {
        remark: remarks.trim(),
        arap_at_hospital: roundedInput,
        dispute_bucket: disputeBucket,
        files: uploadedFiles.map((f) => ({ url: f.url })),
      });
      onClose();
    } catch (err) {
      console.error('Dispute error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && !uploading && onClose()}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : '14px', m: isMobile ? 0 : 2 } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: '15px',
          borderBottom: '1px solid #e3e8f3',
          py: 1.2,
          px: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          {patientName} · <span style={{ color: '#7a84a3', fontWeight: 600, fontSize: '13px' }}>{appId}</span>
        </div>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={submitting || uploading}
          size="small"
          sx={{
            color: '#7a84a3',
            p: 0.5,
            '&:hover': { color: '#0d1f35', background: '#f0f4f9' },
          }}
        >
          <CloseOutlined sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '14px !important', pb: '14px !important', px: isMobile ? 2 : 2.5 }}>
        {/* Compact Top Reference Card */}
        <div className="ar-modal-ref-card">
          <div className="ar-modal-ref-item">
            <small>Hospital Share</small>
            <b style={{ color: '#5a12cc' }}>{inr(hospitalShare)}</b>
          </div>
          <div className="ar-modal-ref-item">
            <small>AR/AP Finance</small>
            <b style={{ color: Number(financeArap) < 0 ? '#b75c00' : '#008a6e' }}>{inr(financeArap)}</b>
          </div>
          <div className="ar-modal-ref-item">
            <small>AR/AP at Hospital</small>
            <b style={{ color: roundedInput < 0 ? '#b75c00' : '#008a6e' }}>{inr(roundedInput)}</b>
          </div>
          <div className="ar-modal-ref-item"><small>Collected at Hospital</small><b style={{ color: '#0d1f35' }}>{inr(caseObj?.collectedAtHospital != null ? caseObj.collectedAtHospital : 0)}</b></div>
          <div className="ar-modal-ref-item"><small>Collected at GHV</small><b style={{ color: '#0d1f35' }}>{inr(caseObj?.collectedAtGHV != null ? caseObj.collectedAtGHV : 0)}</b></div>
        </div>

        {/* AR/AP at Hospital Input + Validate */}
        <Box sx={{ mt: 1.5, mb: 1 }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#0d1f35', mb: 0.5 }}>
            AR/AP at Hospital <span style={{ color: '#c62828' }}>*</span>
          </Typography>
          <div className="ar-modal-input-group">
            <div className="ar-modal-input-wrap">
              <span className="ar-modal-input-prefix">₹</span>
              <input
                type="text"
                inputMode="numeric"
                className="ar-modal-input"
                placeholder="Enter Hospital AR/AP amount"
                value={inputArap}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleValidate();
                  }
                }}
              />
            </div>
            <Button
              variant={hasValidated ? 'outlined' : 'contained'}
              size="small"
              onClick={handleValidate}
              startIcon={hasValidated ? <CheckCircle sx={{ fontSize: 16, color: '#137a3f' }} /> : null}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '12px',
                height: '38px',
                px: 2,
                borderRadius: '8px',
                whiteSpace: 'nowrap',
                background: hasValidated ? '#f0fdf4' : '#3D7CC9',
                borderColor: hasValidated ? '#b7ebd0' : '#3D7CC9',
                color: hasValidated ? '#137a3f' : '#ffffff',
                '&:hover': {
                  background: hasValidated ? '#dcfae6' : '#285e9e',
                  borderColor: hasValidated ? '#86efac' : '#285e9e',
                },
              }}
            >
              {hasValidated ? 'Validated' : 'Validate'}
            </Button>
          </div>
        </Box>

        {/* State: Prompt to Validate if input was changed */}
        {!hasValidated && (
          <div className="ar-diff-banner" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', marginTop: 10 }}>
            <span style={{ fontSize: '12px', fontWeight: 600 }}>Amount modified. Click &quot;Validate&quot; or press Enter to compare amounts.</span>
          </div>
        )}

        {/* State 1: Sign Off Confirmation Branch (Match) */}
        {isMatch && (
          <div className="ar-diff-banner match" style={{ marginTop: 12 }}>
            <CheckCircle sx={{ fontSize: 18, color: '#137a3f', flexShrink: 0 }} />
            <div>
              <span style={{ fontWeight: 700 }}>Amounts Match! Ready for sign-off (Diff: ₹0).</span>
              <div style={{ fontSize: '12px', fontWeight: 400, color: '#1e4620', marginTop: 3 }}>
                Both AR/AP Finance and Hospital amounts are equal ({inr(roundedInput)}). Please confirm to sign off.
              </div>
            </div>
          </div>
        )}

        {/* State 2: Raise Dispute Details Branch (Mismatch) */}
        {isMismatch && (
          <>
            <div className="ar-diff-banner mismatch" style={{ marginTop: 12 }}>
              <WarningAmberOutlined sx={{ fontSize: 18, color: '#b75c00', flexShrink: 0 }} />
              <div>
                <span style={{ fontWeight: 700 }}>Difference: {inr(difference)}</span>
                <span style={{ fontSize: '11px', fontWeight: 400, opacity: 0.9, marginLeft: 6 }}>
                  (Hospital: {inr(roundedInput)} vs Finance: {inr(financeArap)})
                </span>
              </div>
            </div>

            <Box sx={{ mt: 1.5 }}>
              {/* Dispute Bucket */}
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#0d1f35', mb: 0.5 }}>
                Dispute Bucket <span style={{ color: '#c62828' }}>*</span>
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1.2 }}>
                <Select
                  displayEmpty
                  value={disputeBucket}
                  onChange={(e) => setDisputeBucket(e.target.value)}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: '#9aa3bc', fontWeight: 400 }}>Select Dispute Bucket</span>;
                    }
                    return <span style={{ fontWeight: 600, color: '#0d1f35' }}>{selected}</span>;
                  }}
                  sx={{
                    borderRadius: '8px',
                    fontSize: '13px',
                    background: '#ffffff',
                    height: '38px',
                    border: '1px solid #d1d5db',
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '&:hover': {
                      borderColor: '#3D7CC9',
                    },
                    '&.Mui-focused': {
                      borderColor: '#3D7CC9',
                      boxShadow: '0 0 0 3px rgba(61, 124, 201, 0.15)',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: '10px',
                        boxShadow: '0 10px 25px rgba(13, 31, 53, 0.12), 0 2px 6px rgba(13, 31, 53, 0.04)',
                        border: '1px solid #e2e8f0',
                        mt: 0.8,
                        maxHeight: 250,
                        '& .MuiMenuItem-root': {
                          fontSize: '13px',
                          py: 1,
                          px: 1.5,
                          borderRadius: '6px',
                          mx: 0.6,
                          my: 0.2,
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            backgroundColor: '#f0f5ff',
                            color: '#3D7CC9',
                            fontWeight: 600,
                          },
                          '&.Mui-selected': {
                            backgroundColor: '#e6f0fc !important',
                            color: '#285e9e',
                            fontWeight: 700,
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="" disabled sx={{ fontSize: '13px', color: '#9aa3bc' }}>
                    <em>Select Dispute Bucket</em>
                  </MenuItem>
                  {DISPUTE_BUCKETS.map((b) => (
                    <MenuItem key={b} value={b} sx={{ fontSize: '13px' }}>
                      {b}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Remarks */}
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#0d1f35', mb: 0.5 }}>
                Remarks <span style={{ color: '#c62828' }}>*</span>
                <span style={{ color: '#7a84a3', fontWeight: 400 }}> (reason for dispute)</span>
              </Typography>
              <textarea
                className="ar-remarks-textarea"
                rows={3}
                maxLength={500}
                placeholder="Explain why you disagree with this calculation..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                style={{ padding: '8px 10px', fontSize: '12px', borderRadius: '8px' }}
              />
              <Typography sx={{ fontSize: '10px', color: '#7a84a3', mt: 0.2, mb: 1.2, textAlign: 'right' }}>
                {remarks.length}/500
              </Typography>

              {/* Document Upload Area */}
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#0d1f35', mb: 0.5 }}>
                Supporting Documents <span style={{ color: '#7a84a3', fontWeight: 400 }}>(Optional)</span>
              </Typography>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                id="ar-file-upload-input"
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.webp"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                disabled={uploading || submitting}
              />

              {/* Uploaded files list */}
              {uploadedFiles.length > 0 && (
                <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {uploadedFiles.map((file, i) => (
                    <div key={i} className="ar-file-tag">
                      <InsertDriveFileOutlined sx={{ fontSize: 14, color: '#3D7CC9' }} />
                      <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                        {file.name || `Document ${i + 1}`}
                      </span>
                      <IconButton
                        size="small"
                        onClick={() => removeFile(i)}
                        sx={{ p: 0.2, color: '#7a84a3', '&:hover': { color: '#d32f2f', background: '#ffebee' } }}
                      >
                        <CloseOutlined sx={{ fontSize: 13 }} />
                      </IconButton>
                    </div>
                  ))}
                </Box>
              )}

              {/* Dropzone / Upload button */}
              <div
                className={`ar-upload-zone ${isDragOver ? 'dragover' : ''}`}
                onClick={() => !uploading && fileInputRef.current && fileInputRef.current.click()}
                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
                onDrop={handleDrop}
              >
                {uploading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <CircularProgress size={24} sx={{ color: '#3D7CC9' }} />
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3D7CC9' }}>
                      Uploading document(s)...
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.8 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: '50%', background: '#eaf2fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {uploadedFiles.length > 0 ? (
                        <AddOutlined sx={{ fontSize: 20, color: '#3D7CC9' }} />
                      ) : (
                        <CloudUploadOutlined sx={{ fontSize: 20, color: '#3D7CC9' }} />
                      )}
                    </Box>
                    <div>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#0d1f35', textAlign: 'center' }}>
                        {uploadedFiles.length > 0 ? (
                          <>Upload another file or <span style={{ color: '#3D7CC9', textDecoration: 'underline' }}>browse</span></>
                        ) : (
                          <>Drag &amp; drop files here, or <span style={{ color: '#3D7CC9', textDecoration: 'underline' }}>browse</span></>
                        )}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#7a84a3', textAlign: 'center', mt: 0.2 }}>
                        Supports PDF, DOC, XLS, CSV, Images (Max 20MB)
                      </Typography>
                    </div>
                  </Box>
                )}
              </div>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #e3e8f3', py: 1, px: 2.5, gap: 1 }}>
        <Button
          size="small"
          disabled={submitting || uploading}
          onClick={onClose}
          sx={{ textTransform: 'none', borderRadius: '8px', color: '#7a84a3', fontSize: '12px' }}
        >
          Cancel
        </Button>

        {isMatch && (
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={submitting || !hasValidated}
            onClick={handleSignOff}
            startIcon={submitting ? <CircularProgress size={13} color="inherit" /> : <CheckCircleOutline sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, px: 2.5, fontSize: '12px' }}
          >
            {submitting ? 'Signing Off...' : 'Confirm Sign Off'}
          </Button>
        )}

        {isMismatch && (
          <Button
            size="small"
            variant="contained"
            disabled={!remarks.trim() || !disputeBucket || submitting || uploading || !hasValidated}
            onClick={handleRaiseDispute}
            startIcon={submitting ? <CircularProgress size={13} color="inherit" /> : <ReportProblemOutlined sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 700,
              px: 2.5,
              fontSize: '12px',
              background: '#b75c00',
              color: '#fff',
              '&:hover': { background: '#9e4e00' },
              '&.Mui-disabled': { background: '#e0e0e0', color: '#9e9e9e' },
            }}
          >
            {submitting ? 'Submitting...' : 'Raise Dispute'}
          </Button>
        )}

        {!hasValidated && (
          <Button
            size="small"
            variant="contained"
            onClick={handleValidate}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 700,
              px: 2.5,
              fontSize: '12px',
              background: '#3D7CC9',
              color: '#fff',
              '&:hover': { background: '#285e9e' },
            }}
          >
            Validate Amount
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── Case Detail View ───────────────────────────────────────────────────────────
const ARDetail = ({
  appId,
  caseSummary,
  fromTab = 'dash',
  onBack,
}) => {
  const isMobile = useMediaQuery('(max-width:599px)');
  const [loading, setLoading] = useState(true);
  const [billData, setBillData] = useState(null);
  const [docsOpen, setDocsOpen] = useState(false);

  const tabTitleMap = {
    dash: 'AR Reconciliation',
    signed: 'Signed Off Cases',
    disp: 'Raised Disputes',
  };
  const fromTabTitle = tabTitleMap[fromTab] || 'AR Reconciliation';

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    getAppointmentBillInfoService(appId)
      .then((res) => {
        if (!isMounted) return;
        if (res && res.data) {
          setBillData(res.data);
        }
      })
      .catch((err) => {
        console.error('getAppointmentBillInfo error:', err);
        toast.error('Could not load bill info, showing summary data');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [appId]);

  const patientName = billData?.patientName || caseSummary?.patientName || '--';
  const hospitalName = billData?.hospitalName || caseSummary?.hospitalName || '--';
  const doctorName = billData?.doctorName || '--';
  const diseaseName = billData?.diseaseName || caseSummary?.disease || '--';
  const categoryName = billData?.categoryName || '--';
  const paymentMode = billData?.PaymentMode || '--';
  const lensDetails = [billData?.lensBrand, billData?.lensModel].filter(Boolean).join(' - ') || '--';
  const dateAdm = fmtDate(billData?.dateOfAdmission || caseSummary?.dateOfAdmission || caseSummary?.apd || caseSummary?.admission_date, { includeTime: true });
  const dateDsc = fmtDate(billData?.dateOfDischarge, { includeTime: true });
  const totalRevenue = inr(billData?.netBillAmount ?? caseSummary?.total_revenue);

  const calc = billData?.calculation || {};
  const calcInputs = calc.inputs || {};
  const calcResult = calc.result || {};

  const extraCash = inr(billData?.extraCash ?? calcInputs?.Extra_Cash);
  const approvedAmt = inr(billData?.approvedAmount ?? calcInputs?.Approved_Amount);

  const hospitalShare = inr(calcResult.hospital_share_total ?? caseSummary?.hospital_share_total ?? caseSummary?.hs);
  const ghvShare = inr(calcResult.ghv_share_total ?? caseSummary?.ghv_share_total ?? caseSummary?.gs);
  const settledAmt = inr(calcResult.settlement_amount);
  const hvCost = inr(calcResult.high_value_implant_cost);
  const equipHire = inr(calcResult.equipment_hire_charges);
  const sharingAmt = inr(calcResult.sharing_amount);
  const baseShare = inr(calcResult.hospital_share_base);
  const baseBasis = calcResult.hospital_share_basis || '--';
  const addonsTotal = inr(calcResult.clause_d_charges);

  const steps = calcResult.calculation_steps || [];
  const attachments = billData?.attachments || [];
  const totalDocsCount = attachments.reduce((sum, att) => {
    if (Array.isArray(att.urls)) return sum + att.urls.length;
    if (att.url) return sum + 1;
    return sum;
  }, 0);

  const combinedCase = {
    ...caseSummary,
    ...billData,
    status: billData?.status || caseSummary?.status,
    hospitalStatus: billData?.hospitalStatus || caseSummary?.hospitalStatus,
    actionTrail: billData?.actionTrail || caseSummary?.actionTrail,
  };
  const statusInfo = arStatusInfo(
    combinedCase,
    fromTab === 'signed' ? 'signed_off' : fromTab === 'disp' ? 'dispute' : ''
  );

  const approveItem = getLastActionItem(combinedCase, 'hospital_approve');
  const disputeItem = getLastActionItem(combinedCase, 'hospital_dispute');

  const infoItem = (label, value, sx = {}) => (
    <div className="ar-info-item">
      <small>{label}</small>
      <b style={sx}>{value}</b>
    </div>
  );

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack sx={{ fontSize: '18px' }} />}
          onClick={onBack}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '13px',
            borderRadius: '8px',
            borderColor: '#dde3ef',
            color: '#00263E',
            background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            px: 2,
            py: 0.6,
            '&:hover': {
              background: '#f4f6fa',
              borderColor: '#3D7CC9',
              color: '#3D7CC9',
            },
          }}
        >
          Back
        </Button>
        <div className="ar-breadcrumb" style={{ margin: 0 }}>
          <span className="link" onClick={onBack}>{fromTabTitle}</span>
          <span>›</span>
          <span style={{ color: '#0d1f35', fontWeight: 600 }}>{appId}</span>
        </div>
      </Box>

      {loading ? (
        <div className="ar-loading-box">
          <CircularProgress size={36} sx={{ color: '#3D7CC9' }} />
          <span>Loading appointment bill information for {appId}...</span>
        </div>
      ) : (
        <>
          {/* Patient & Visit Info */}
          <div className="ar-panel">
            <div className="ar-panel-title">Patient &amp; Visit Information</div>
            <div className="ar-info-grid">
              {infoItem('Date of Admission', dateAdm)}
              {infoItem('Date of Discharge', dateDsc)}
              {infoItem('Hospital Status', <span className={`ar-pill ${statusInfo.cls}`}>{statusInfo.label}</span>)}
              {infoItem('Hospital Name', hospitalName)}
              {infoItem('Doctor Name', doctorName)}
              {infoItem('Disease', diseaseName)}
              {infoItem('Procedure / Category', categoryName)}
              {infoItem('Payment Mode', paymentMode, { color: '#5a12cc', fontWeight: 700 })}
              {infoItem('Lens Details', lensDetails)}
              {infoItem('Patient Name', patientName)}
              {infoItem('Total Revenue', totalRevenue)}
              {infoItem('Collected at Hospital', inr(billData?.collectedAtHospital ?? caseSummary?.collectedAtHospital ?? 0))}
              {infoItem('Collected at GHV', inr(billData?.collectedAtGHV ?? caseSummary?.collectedAtGHV ?? 0))}
              {infoItem('Extra Cash / Refund', extraCash)}
              {infoItem('Approved Amount', approvedAmt)}
            </div>
          </div>

          {/* Dispute Details if disputed */}
          {disputeItem && (
            <div className="ar-panel" style={{ borderLeft: '4px solid #b75c00' }}>
              <div className="ar-panel-title" style={{ color: '#b75c00' }}>Hospital Dispute Details</div>
              <div className="ar-info-grid">
                {infoItem('Dispute Bucket', disputeItem.dispute_bucket || disputeItem.disputeBucket || '--')}
                {infoItem('Hospital AR/AP', disputeItem.arap_at_hospital != null ? inr(disputeItem.arap_at_hospital) : '--', {
                  color: Number(disputeItem.arap_at_hospital) < 0 ? '#b75c00' : '#008a6e',
                  fontWeight: 700,
                })}
                {infoItem('Raised On', fmtDate(disputeItem.timestamp, { includeTime: true }))}
                {infoItem('Raised By', disputeItem.userName || disputeItem.username || disputeItem.user_name || '--')}
                {(disputeItem.remark || disputeItem.remarks) &&
                  infoItem('Remarks', disputeItem.remark || disputeItem.remarks, { gridColumn: '1 / -1' })}
                {Array.isArray(disputeItem.files) && disputeItem.files.length > 0 && (
                  <div className="ar-info-item" style={{ gridColumn: '1 / -1' }}>
                    <small>Uploaded Files</small>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                      {disputeItem.files.map((f, i) => {
                        const url = typeof f === 'string' ? f : (f?.url || f?.URL || '');
                        if (!url) return null;
                        return (
                          <Button
                            key={i}
                            size="small"
                            variant="outlined"
                            startIcon={<DescriptionOutlined sx={{ fontSize: 16 }} />}
                            onClick={() => openSignedUrl(url)}
                            sx={{
                              textTransform: 'none',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: 600,
                              borderColor: '#3D7CC9',
                              color: '#3D7CC9',
                              whiteSpace: 'nowrap',
                              '&:hover': { background: '#eef4ff', borderColor: '#3D7CC9' },
                            }}
                          >
                            Document {i + 1}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sign-off Details if approved */}
          {approveItem && (
            <div className="ar-panel" style={{ borderLeft: '4px solid #008a6e' }}>
              <div className="ar-panel-title" style={{ color: '#008a6e' }}>Hospital Sign-off Details</div>
              <div className="ar-info-grid">
                {infoItem('Signed Off On', fmtDate(approveItem.timestamp, { includeTime: true }))}
                {infoItem('Signed By', approveItem.userName || approveItem.username || approveItem.user_name || '--')}
                {(approveItem.remark || approveItem.remarks) &&
                  infoItem('Remarks', approveItem.remark || approveItem.remarks, { gridColumn: '1 / -1' })}
              </div>
            </div>
          )}

          {/* Share cards */}
          <div className="ar-cards-row">
            <div className="ar-share-card hospital">
              <small>Hospital Share</small>
              <div className="ar-amount">{hospitalShare}</div>
            </div>
            <div className="ar-share-card ghv">
              <small>AR/AP Finance</small>
              <div className="ar-amount">{ghvShare}</div>
            </div>
          </div>

          {/* Calculation Summary */}
          <div className="ar-panel">
            <div className="ar-panel-title">Calculation Summary</div>
            <div className="ar-calc-grid">
              <span className="ar-label">Settled Amount</span>
              <span className="ar-value">{settledAmt}</span>

              <span className="ar-label">HV Cost / Implants</span>
              <span className="ar-value">{hvCost}</span>

              <span className="ar-label">Equipment Hire</span>
              <span className="ar-value">{equipHire}</span>

              <span className="ar-label">Sharing Amount</span>
              <span className="ar-value">{sharingAmt}</span>

              <span className="ar-label">Hospital Share Base</span>
              <span className="ar-value">{baseShare}</span>

              {baseBasis && baseBasis !== '--' && (
                <>
                  <span className="ar-label">Basis of Sharing</span>
                  <span className="ar-value" style={{ fontSize: '11px', color: '#7a84a3', fontWeight: 500 }}>
                    {baseBasis}
                  </span>
                </>
              )}

              <span className="ar-label">Add-ons Total</span>
              <span className="ar-value">{addonsTotal}</span>

              <Divider sx={{ gridColumn: '1/-1', my: 0.5, borderColor: '#e3e8f3' }} />

              <span className="ar-label ar-total-row" style={{ fontWeight: 700 }}>Hospital Share</span>
              <span className="ar-value ar-total-row" style={{ color: '#5a12cc', fontSize: '16px' }}>{hospitalShare}</span>

              <span className="ar-label" style={{ fontWeight: 700 }}>AR/AP Finance</span>
              <span className="ar-value" style={{ color: '#008a6e', fontSize: '16px' }}>{ghvShare}</span>
            </div>
          </div>

          {/* Step-by-step Calculation */}
          {steps.length > 0 && (
            <div className="ar-panel">
              <div className="ar-panel-title">Calculation Detail — Step by Step</div>
              {steps.map((st, i) => (
                <div key={i} className={`ar-step ${st.highlight || ''}`}>
                  <div className="ar-step-title">{st.title}</div>
                  <div className="ar-step-body">{st.formula}</div>
                </div>
              ))}
            </div>
          )}

          {/* View Documents Button */}
          <div className="ar-actions">
            <Button
              variant="outlined"
              startIcon={<DescriptionOutlined />}
              onClick={() => setDocsOpen(true)}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                fontWeight: 600,
                px: 3,
                borderColor: '#3D7CC9',
                color: '#3D7CC9',
                '&:hover': { background: '#eef4ff', borderColor: '#3D7CC9' },
                ...(isMobile ? { width: '100%' } : {}),
              }}
            >
              View Supporting Documents ({totalDocsCount || '0'})
            </Button>
          </div>

          {/* Supporting Docs Modal */}
          <DocsModal
            open={docsOpen}
            onClose={() => setDocsOpen(false)}
            attachments={attachments}
            appId={appId}
            patientName={patientName}
          />
        </>
      )}
    </>
  );
};

// ─── Shared Pagination Bar ──────────────────────────────────────────────────
const PaginationBar = ({ page, totalPages, onChange }) =>
  totalPages <= 1 ? null : (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2, borderTop: '1px solid #edf0f7', mt: 0.5 }}>
      <Pagination
        count={totalPages}
        page={page}
        onChange={(_, val) => onChange(val)}
        color="primary"
        size="small"
        showFirstButton
        showLastButton
        sx={{
          '& .MuiPaginationItem-root': { fontSize: '13px', fontWeight: 600, borderRadius: '8px' },
          '& .Mui-selected': { background: '#3D7CC9 !important', color: '#fff' },
        }}
      />
    </Box>
  );

// ─── Main Dashboard (AR Reconciliation List) ──────────────────────────────────
const ARDashboard = ({
  cases,
  loading,
  onRefresh,
  onView,
  openCount,
  signedCount,
  disputeCount,
  onTabSelect,
  hospitalSelector,
  onCaseAction,
  canAction = true,
}) => {
  const [filters, setFilters] = useState({ app: '', pt: '', from: '', to: '' });
  const [page, setPage] = useState(1);
  const [actionConfig, setActionConfig] = useState({ caseObj: null, type: '', hospitalArap: 0 });
  const [rowArapValues, setRowArapValues] = useState({});

  const upd = (key) => (e) => { setPage(1); setFilters((p) => ({ ...p, [key]: e.target.value })); };
  const reset = () => { setPage(1); setFilters({ app: '', pt: '', from: '', to: '' }); };

  const fromYmd = filters.from ? toIstYmd(filters.from) || filters.from : '';
  const toYmd = filters.to ? toIstYmd(filters.to) || filters.to : '';
  const queryApp = (filters.app || '').trim().toLowerCase();
  const queryPt = (filters.pt || '').trim().toLowerCase();

  const filtered = cases.filter((c) => {
    const appId = String(c.appId || c.app || c.aid || '');
    const pt = String(c.patientName || c.pt || c.patient_name || '');
    const caseDate = getCaseDateYmd(c);

    const matchesApp = !queryApp || appId.toLowerCase().includes(queryApp);
    const matchesPt = !queryPt || pt.toLowerCase().includes(queryPt);
    const matchesFrom = !fromYmd || (caseDate && caseDate >= fromYmd);
    const matchesTo = !toYmd || (caseDate && caseDate <= toYmd);

    return matchesApp && matchesPt && matchesFrom && matchesTo;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const tableContainerRef = useRef(null);

  const handleArapInputChange = (appId, val) => {
    if (val !== '' && val !== '-' && !/^-?\d*\.?\d*$/.test(val)) {
      return;
    }
    setRowArapValues((prev) => ({ ...prev, [appId]: val }));
  };

  const hasAnyEnteredArap = Object.values(rowArapValues).some(
    (v) => v !== undefined && String(v).trim() !== '' && !isNaN(Number(v))
  );

  return (
    <>
      <div className="ar-page-header">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 1 }}>
          <div>
            <Typography className="ar-page-title">AR Reconciliation</Typography>
            <Typography className="ar-page-sub">
              Cases shared by Pristyn Care awaiting your review and sign-off
            </Typography>
          </div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
            {hospitalSelector}
            <Tooltip title="Refresh list">
              <IconButton onClick={onRefresh} sx={{ color: '#3D7CC9', background: '#fff', border: '1px solid #dde3ef' }}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </div>

      <StatsRow
        openCount={openCount}
        signedCount={signedCount}
        disputeCount={disputeCount}
        onTabSelect={onTabSelect}
      />

      {/* Filter Bar */}
      <ARFilterBar
        title="Filter Cases"
        filters={filters}
        onChange={upd}
        onReset={reset}
      />

      {loading ? (
        <div className="ar-loading-box">
          <CircularProgress size={32} sx={{ color: '#3D7CC9' }} />
          <span>Fetching AR Reconciliation cases...</span>
        </div>
      ) : (
        <div className="ar-table-card">
          <div className="ar-table-header">
            <span className="ar-table-header-title">
              {filtered.length} case{filtered.length !== 1 ? 's' : ''} found
              {totalPages > 1 && <span style={{ fontWeight: 400, color: '#7a84a3', fontSize: '12px', marginLeft: 8 }}>· Page {page} of {totalPages}</span>}
            </span>
          </div>
          <TableContainer ref={tableContainerRef} className="ar-table-wrap">
            <Table size="small" sx={{ minWidth: 1480 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={stickyAppThSx} className="ar-sticky-col-app-th">App ID</TableCell>
                  <TableCell sx={stickyPtThSx} className="ar-sticky-col-pt-th">Patient Name</TableCell>
                  <TableCell sx={{ ...thSx, pl: 3, minWidth: 175 }} className="ar-disease-th">Disease</TableCell>
                  <TableCell sx={thSx}>Admission Date</TableCell>
                    <TableCell sx={thSx}>Total Revenue</TableCell>
                    <TableCell sx={thSx}>Collected at Hospital</TableCell>
                    <TableCell sx={thSx}>Collected at GHV</TableCell>
                    <TableCell sx={thSx}>Hospital Share</TableCell>
                    <TableCell sx={thSx}>AR/AP Finance</TableCell>
                    <TableCell sx={{ ...thSx, minWidth: 150 }}>AR/AP at Hospital</TableCell>
                    {hasAnyEnteredArap && (
                      <TableCell sx={{ ...thSx, minWidth: 130 }}>Difference</TableCell>
                    )}
                    <TableCell sx={thSx}>Status</TableCell>
                    <TableCell sx={{ ...thSx, minWidth: 220 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={hasAnyEnteredArap ? 13 : 12} className="ar-empty">
                        No pending cases match your filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((c, idx) => {
                      const appId = c.appId || c.app;
                      const pt = c.patientName || c.pt || '--';
                      const disease = c.disease || '--';
                      const apd = fmtDate(c.dateOfAdmission || c.apd || c.admission_date || c.date_of_admission || c.doa || c.admissionDate, { includeTime: true });
                      const totalRev = c.total_revenue != null ? c.total_revenue : null;
                      const hs = c.hospital_share_total != null ? c.hospital_share_total : (c.hs || 0);
                      const gs = c.ghv_share_total != null ? c.ghv_share_total : (c.gs || 0);
                      const st = arStatusInfo(c);
                      const isPending = st.cls === 'pending';

                      // AR/AP inline calculations
                      const enteredVal = rowArapValues[appId] ?? '';
                      const parsed = enteredVal.trim() === '' ? null : Number(enteredVal);
                      const isValid = parsed !== null && !isNaN(parsed);
                      const roundedInput = isValid ? Math.round(parsed) : 0;
                      const roundedFinance = Math.round(Number(gs) || 0);

                      const isMatch = isValid && roundedInput === roundedFinance;
                      const diffVal = isValid ? roundedFinance - roundedInput : null;
                      const isMismatch = isValid && !isMatch;

                      return (
                        <TableRow
                          key={appId}
                          hover
                          sx={{
                            '&:last-child td': { borderBottom: 0 },
                            background: idx % 2 === 0 ? '#fff' : '#fafbfd',
                            '&:hover': { background: '#eef4ff !important' },
                          }}
                        >
                          <TableCell
                            className="ar-sticky-col-app-td"
                            sx={{
                              ...stickyAppTdSx,
                              color: '#3D7CC9',
                              fontWeight: 700,
                              fontSize: '13px',
                              py: 1.5,
                              px: 1.5,
                              background: idx % 2 === 0 ? '#fff' : '#fafbfd',
                            }}
                            title={appId}
                          >
                            {appId}
                          </TableCell>
                          <TableCell
                            className="ar-sticky-col-pt-td"
                            sx={{
                              ...stickyPtTdSx,
                              fontWeight: 500,
                              fontSize: '13px',
                              py: 1.5,
                              px: 1.5,
                              background: idx % 2 === 0 ? '#fff' : '#fafbfd',
                            }}
                            title={pt}
                          >
                            {pt}
                          </TableCell>
                          <TableCell
                            className="ar-disease-td"
                            sx={{
                              color: '#0d1f35',
                              fontSize: '13px',
                              py: 1.5,
                              pl: 3,
                              pr: 2,
                              minWidth: 175,
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {disease}
                          </TableCell>
                          <TableCell className="ar-date-cell" sx={{ color: '#7a84a3', fontSize: '13px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>
                            {apd}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#0d1f35', py: 1.5, px: 1.5 }}>
                            {totalRev != null ? inr(totalRev) : '--'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#0d1f35', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>{inr(c.collectedAtHospital != null ? c.collectedAtHospital : 0)}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#0d1f35', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>{inr(c.collectedAtGHV != null ? c.collectedAtGHV : 0)}</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '13px', color: '#5a12cc', py: 1.5, px: 1.5 }}>
                            {inr(hs)}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '13px', color: Number(gs) < 0 ? '#b75c00' : '#008a6e', py: 1.5, px: 1.5 }}>
                            {inr(gs)}
                          </TableCell>

                          {/* Inline AR/AP Input Column */}
                          <TableCell
                            sx={{ py: 1, px: 1.5, minWidth: 145 }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            {canAction && isPending ? (
                              <TextField
                                size="small"
                                type="text"
                                placeholder="Enter AR/AP"
                                value={enteredVal}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                inputProps={{ inputMode: 'numeric' }}
                                onChange={(e) => handleArapInputChange(appId, e.target.value)}
                                sx={{
                                  width: 130,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                    background: '#fff',
                                    fontWeight: 600,
                                    height: '34px',
                                    '& fieldset': {
                                      borderColor: isValid
                                        ? (isMatch ? '#2e7d32' : '#b75c00')
                                        : '#dde3ef',
                                      borderWidth: isValid ? '1.5px' : '1px',
                                    },
                                  },
                                }}
                              />
                            ) : (
                              <span style={{ color: '#7a84a3', fontSize: '12px' }}>--</span>
                            )}
                          </TableCell>

                          {/* Difference Column - rendered only when input is filled */}
                          {hasAnyEnteredArap && (
                            <TableCell
                              sx={{ py: 1.5, px: 1.5, minWidth: 120 }}
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              {!isValid ? (
                                <span style={{ color: '#9aa3bc', fontSize: '12px' }}>--</span>
                              ) : isMatch ? (
                                <span style={{ color: '#137a3f', fontWeight: 700, fontSize: '11px', background: '#dff5e8', padding: '3px 8px', borderRadius: '6px', whiteSpace: 'nowrap' }}>
                                  ₹0 (Matched)
                                </span>
                              ) : (
                                <span style={{ color: '#b75c00', fontWeight: 700, fontSize: '11px', background: '#fff4e5', padding: '3px 8px', borderRadius: '6px', whiteSpace: 'nowrap' }}>
                                  {inr(diffVal)}
                                </span>
                              )}
                            </TableCell>
                          )}

                          <TableCell sx={{ py: 1.5, px: 1.5 }}>
                            <span className={`ar-pill ${st.cls}`}>{st.label}</span>
                          </TableCell>
                          <TableCell
                            sx={{ py: 1.5, px: 1.5, minWidth: 220 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => onView(appId, c)}
                                sx={{
                                  textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                                  fontWeight: 600, borderColor: '#3D7CC9', color: '#3D7CC9',
                                  whiteSpace: 'nowrap', '&:hover': { background: '#eef4ff', borderColor: '#3D7CC9' },
                                }}
                              >
                                View Details
                              </Button>

                              {canAction && isPending && isValid && isMatch && (
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={() => setActionConfig({ caseObj: c, type: 'hospital_approve', hospitalArap: roundedInput })}
                                  startIcon={<CheckCircleOutline sx={{ fontSize: 14 }} />}
                                  sx={{
                                    textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                                    fontWeight: 700, whiteSpace: 'nowrap',
                                  }}
                                >
                                  Sign Off
                                </Button>
                              )}

                              {canAction && isPending && isValid && isMismatch && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => setActionConfig({ caseObj: c, type: 'hospital_dispute', hospitalArap: roundedInput })}
                                  startIcon={<ReportProblemOutlined sx={{ fontSize: 14 }} />}
                                  sx={{
                                    textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                                    fontWeight: 700, background: '#b75c00', color: '#fff',
                                    whiteSpace: 'nowrap', '&:hover': { background: '#9e4e00' },
                                  }}
                                >
                                  Raise Dispute
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <PaginationBar page={page} totalPages={totalPages} onChange={setPage} />
          </div>
      )}

      {/* Action Modal */}
      <ActionModal
        open={Boolean(actionConfig.caseObj)}
        onClose={() => setActionConfig({ caseObj: null, type: '', hospitalArap: 0 })}
        caseObj={actionConfig.caseObj}
        actionType={actionConfig.type}
        hospitalArap={actionConfig.hospitalArap}
        onActionSuccess={onCaseAction}
      />
    </>
  );
};

// ─── Signed Off Cases View ────────────────────────────────────────────────────
const ARSignedOff = ({ cases, loading, onRefresh, onView, hospitalSelector }) => {
  const [filters, setFilters] = useState({ app: '', pt: '', from: '', to: '' });
  const [page, setPage] = useState(1);

  const upd = (key) => (e) => { setPage(1); setFilters((p) => ({ ...p, [key]: e.target.value })); };
  const reset = () => { setPage(1); setFilters({ app: '', pt: '', from: '', to: '' }); };

  const fromYmd = filters.from ? toIstYmd(filters.from) || filters.from : '';
  const toYmd = filters.to ? toIstYmd(filters.to) || filters.to : '';
  const queryApp = (filters.app || '').trim().toLowerCase();
  const queryPt = (filters.pt || '').trim().toLowerCase();

  const filtered = cases.filter((c) => {
    const appId = String(c.appId || c.app || c.aid || '');
    const pt = String(c.patientName || c.pt || c.patient_name || '');
    const caseDate = getCaseDateYmd(c);

    const matchesApp = !queryApp || appId.toLowerCase().includes(queryApp);
    const matchesPt = !queryPt || pt.toLowerCase().includes(queryPt);
    const matchesFrom = !fromYmd || (caseDate && caseDate >= fromYmd);
    const matchesTo = !toYmd || (caseDate && caseDate <= toYmd);

    return matchesApp && matchesPt && matchesFrom && matchesTo;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="ar-page-header">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 1 }}>
          <div>
            <Typography className="ar-page-title">Signed Off Cases</Typography>
            <Typography className="ar-page-sub">Read-only archive of hospital approved and finalized cases</Typography>
          </div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
            {hospitalSelector}
            <Tooltip title="Refresh list">
              <IconButton onClick={onRefresh} sx={{ color: '#3D7CC9', background: '#fff', border: '1px solid #dde3ef' }}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </div>

      {/* Filter Bar */}
      <ARFilterBar
        title="Filter Signed Off Cases"
        filters={filters}
        onChange={upd}
        onReset={reset}
      />

      {loading ? (
        <div className="ar-loading-box">
          <CircularProgress size={32} sx={{ color: '#137a3f' }} />
          <span>Fetching signed off cases...</span>
        </div>
      ) : cases.length === 0 ? (
        <div className="ar-panel"><div className="ar-empty">No signed-off cases found.</div></div>
      ) : (
        <div className="ar-table-card">
          <div className="ar-table-header">
            <span className="ar-table-header-title">
              {filtered.length} signed-off case{filtered.length !== 1 ? 's' : ''}
              {totalPages > 1 && <span style={{ fontWeight: 400, color: '#7a84a3', fontSize: '12px', marginLeft: 8 }}>· Page {page} of {totalPages}</span>}
            </span>
          </div>
          <TableContainer className="ar-table-wrap">
            <Table size="small" sx={{ minWidth: 1480 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={stickyAppThSx} className="ar-sticky-col-app-th">App ID</TableCell>
                  <TableCell sx={stickyPtThSx} className="ar-sticky-col-pt-th">Patient Name</TableCell>
                  <TableCell sx={{ ...thSx, pl: 3, minWidth: 175 }} className="ar-disease-th">Disease</TableCell>
                  <TableCell sx={thSx}>Admission Date</TableCell>
                  <TableCell sx={thSx}>Total Revenue</TableCell>
                  <TableCell sx={thSx}>Collected at Hospital</TableCell>
                  <TableCell sx={thSx}>Collected at GHV</TableCell>
                  <TableCell sx={thSx}>Hospital Share</TableCell>
                  <TableCell sx={thSx}>AR/AP Finance</TableCell>
                  <TableCell sx={thSx}>Signed Off On</TableCell>
                  <TableCell sx={thSx}>Signed By</TableCell>
                  <TableCell sx={thSx}>Status</TableCell>
                  <TableCell sx={thStickySx} className="ar-sticky-action-th">Action</TableCell>
                </TableRow>
              </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} className="ar-empty">
                        No signed-off cases match your filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((c, idx) => {
                      const appId = c.appId || c.app;
                      const pt = c.patientName || c.pt || '--';
                      const disease = c.disease || '--';
                      const apd = fmtDate(c.dateOfAdmission || c.apd || c.admission_date || c.date_of_admission || c.doa || c.admissionDate, { includeTime: true });
                      const totalRev = c.total_revenue != null ? c.total_revenue : null;
                      const hs = c.hospital_share_total != null ? c.hospital_share_total : (c.hs || 0);
                      const gs = c.ghv_share_total != null ? c.ghv_share_total : (c.gs || 0);

                      const approveItem = getLastActionItem(c, 'hospital_approve');
                      const signedOnDate = approveItem?.timestamp || c.updated_at || c.signedOffAt || c.action_date || (c.hospitalActionTrail && c.hospitalActionTrail[c.hospitalActionTrail.length - 1]?.created_at) || c.created_at;
                      const signedOn = fmtDate(signedOnDate, { includeTime: true });
                      const signedBy = approveItem?.userName || approveItem?.username || approveItem?.user_name || c.userName || c.signedBy || '--';
                      const statusInfo = arStatusInfo(c, 'signed_off');

                      return (
                        <TableRow
                          key={appId}
                          hover
                          sx={{
                            '&:last-child td': { borderBottom: 0 },
                            background: idx % 2 === 0 ? '#fff' : '#fafbfd',
                            '&:hover': { background: '#eef4ff !important' },
                          }}
                        >
                          <TableCell
                            className="ar-sticky-col-app-td"
                            sx={{
                              ...stickyAppTdSx,
                              fontWeight: 700,
                              color: '#3D7CC9',
                              fontSize: '13px',
                              py: 1.6,
                              px: 1.5,
                              background: idx % 2 === 0 ? '#fff' : '#fafbfd',
                            }}
                            title={appId}
                          >
                            {appId}
                          </TableCell>
                          <TableCell
                            className="ar-sticky-col-pt-td"
                            sx={{
                              ...stickyPtTdSx,
                              fontWeight: 500,
                              fontSize: '13px',
                              py: 1.6,
                              px: 1.5,
                              background: idx % 2 === 0 ? '#fff' : '#fafbfd',
                            }}
                            title={pt}
                          >
                            {pt}
                          </TableCell>
                          <TableCell
                            className="ar-disease-td"
                            sx={{
                              color: '#0d1f35',
                              fontSize: '13px',
                              py: 1.6,
                              pl: 3,
                              pr: 2,
                              minWidth: 175,
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {disease}
                          </TableCell>
                          <TableCell className="ar-date-cell" sx={{ color: '#7a84a3', fontSize: '13px', py: 1.8, px: 2, whiteSpace: 'nowrap' }}>{apd}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#0d1f35', py: 1.8, px: 2 }}>{totalRev != null ? inr(totalRev) : '--'}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#0d1f35', py: 1.8, px: 2, whiteSpace: 'nowrap' }}>{inr(c.collectedAtHospital != null ? c.collectedAtHospital : 0)}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#0d1f35', py: 1.8, px: 2, whiteSpace: 'nowrap' }}>{inr(c.collectedAtGHV != null ? c.collectedAtGHV : 0)}</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '13px', color: '#5a12cc', py: 1.8, px: 2 }}>{inr(hs)}</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '13px', color: Number(gs) < 0 ? '#b75c00' : '#008a6e', py: 1.8, px: 2 }}>{inr(gs)}</TableCell>
                          <TableCell className="ar-date-cell" sx={{ color: '#7a84a3', fontSize: '12px', py: 1.8, px: 2, whiteSpace: 'nowrap' }}>{signedOn}</TableCell>
                          <TableCell sx={{ color: '#0d1f35', fontWeight: 600, fontSize: '13px', py: 1.8, px: 2, whiteSpace: 'nowrap' }}>{signedBy}</TableCell>
                          <TableCell sx={{ py: 1.8, px: 2 }}>
                            <span className={`ar-pill ${statusInfo.cls}`}>{statusInfo.label}</span>
                          </TableCell>
                          <TableCell sx={tdStickySx} className="ar-sticky-action-td" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => onView(appId, c)}
                              sx={{
                                textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                                fontWeight: 600, borderColor: '#3D7CC9', color: '#3D7CC9',
                                whiteSpace: 'nowrap', '&:hover': { background: '#eef4ff', borderColor: '#3D7CC9' },
                              }}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <PaginationBar page={page} totalPages={totalPages} onChange={setPage} />
          </div>
      )}
    </>
  );
};

// ─── Raised Disputes View ─────────────────────────────────────────────────────
const ARDisputes = ({ cases, loading, onRefresh, onView, hospitalSelector }) => {
  const [filters, setFilters] = useState({ app: '', pt: '', from: '', to: '' });
  const [page, setPage] = useState(1);

  const upd = (key) => (e) => { setPage(1); setFilters((p) => ({ ...p, [key]: e.target.value })); };
  const reset = () => { setPage(1); setFilters({ app: '', pt: '', from: '', to: '' }); };

  const fromYmd = filters.from ? toIstYmd(filters.from) || filters.from : '';
  const toYmd = filters.to ? toIstYmd(filters.to) || filters.to : '';
  const queryApp = (filters.app || '').trim().toLowerCase();
  const queryPt = (filters.pt || '').trim().toLowerCase();

  const filtered = cases.filter((c) => {
    const appId = String(c.appId || c.app || c.aid || '');
    const pt = String(c.patientName || c.pt || c.patient_name || '');
    const caseDate = getCaseDateYmd(c);

    const matchesApp = !queryApp || appId.toLowerCase().includes(queryApp);
    const matchesPt = !queryPt || pt.toLowerCase().includes(queryPt);
    const matchesFrom = !fromYmd || (caseDate && caseDate >= fromYmd);
    const matchesTo = !toYmd || (caseDate && caseDate <= toYmd);

    return matchesApp && matchesPt && matchesFrom && matchesTo;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="ar-page-header">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 1 }}>
          <div>
            <Typography className="ar-page-title">Raised Disputes</Typography>
            <Typography className="ar-page-sub">Disputes raised by you, actively under review by Pristyn Care</Typography>
          </div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
            {hospitalSelector}
            <Tooltip title="Refresh list">
              <IconButton onClick={onRefresh} sx={{ color: '#3D7CC9', background: '#fff', border: '1px solid #dde3ef' }}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </div>

      {/* Filter Bar */}
      <ARFilterBar
        title="Filter Raised Disputes"
        filters={filters}
        onChange={upd}
        onReset={reset}
      />

      {loading ? (
        <div className="ar-loading-box">
          <CircularProgress size={32} sx={{ color: '#b75c00' }} />
          <span>Fetching raised disputes...</span>
        </div>
      ) : cases.length === 0 ? (
        <div className="ar-panel"><div className="ar-empty">No disputes raised yet.</div></div>
      ) : (
        <div className="ar-table-card">
          <div className="ar-table-header">
            <span className="ar-table-header-title">
              {filtered.length} dispute{filtered.length !== 1 ? 's' : ''} raised
              {totalPages > 1 && <span style={{ fontWeight: 400, color: '#7a84a3', fontSize: '12px', marginLeft: 8 }}>· Page {page} of {totalPages}</span>}
            </span>
          </div>
          <TableContainer className="ar-table-wrap">
            <Table size="small" sx={{ minWidth: 1680 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={stickyAppThSx} className="ar-sticky-col-app-th">App ID</TableCell>
                  <TableCell sx={stickyPtThSx} className="ar-sticky-col-pt-th">Patient Name</TableCell>
                  <TableCell sx={{ ...thSx, pl: 3, minWidth: 175 }} className="ar-disease-th">Disease</TableCell>
                  <TableCell sx={thSx}>Admission Date</TableCell>
                  <TableCell sx={thSx}>Total Revenue</TableCell>
                  <TableCell sx={thSx}>Collected at Hospital</TableCell>
                  <TableCell sx={thSx}>Collected at GHV</TableCell>
                  <TableCell sx={thSx}>Hospital Share</TableCell>
                  <TableCell sx={thSx}>AR/AP Finance</TableCell>
                  <TableCell sx={thSx}>Hospital AR/AP</TableCell>
                  <TableCell sx={thSx}>Dispute Bucket</TableCell>
                  <TableCell sx={thSx}>Remarks</TableCell>
                  <TableCell sx={thSx}>Raised On</TableCell>
                  <TableCell sx={thSx}>Raised By</TableCell>
                  <TableCell sx={thSx}>Status</TableCell>
                  <TableCell sx={thStickySx} className="ar-sticky-action-th">Action</TableCell>
                </TableRow>
              </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={16} className="ar-empty">
                        No disputes match your filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((c, idx) => {
                      const appId = c.appId || c.app;
                      const pt = c.patientName || c.pt || '--';
                      const disease = c.disease || '--';
                      const apd = fmtDate(c.dateOfAdmission || c.apd || c.admission_date || c.date_of_admission || c.doa || c.admissionDate, { includeTime: true });
                      const totalRev = c.total_revenue != null ? c.total_revenue : null;
                      const hs = c.hospital_share_total != null ? c.hospital_share_total : (c.hs || 0);
                      const gs = c.ghv_share_total != null ? c.ghv_share_total : (c.gs || 0);

                      const disputeItem = getLastActionItem(c, 'hospital_dispute');
                      const hospitalArapVal = disputeItem?.arap_at_hospital != null
                        ? disputeItem.arap_at_hospital
                        : (c.arap_at_hospital != null ? c.arap_at_hospital : (c.hospitalArap != null ? c.hospitalArap : (c.hospital_arap != null ? c.hospital_arap : null)));
                      const disputeBucketVal = disputeItem?.dispute_bucket || disputeItem?.disputeBucket || c.dispute_bucket || c.disputeBucket || c.bucket || '--';
                      const lastRemark = disputeItem?.remark || c.remark || c.remarks || c.rm || 'Dispute under review';
                      const raisedOnDate = disputeItem?.timestamp || c.updated_at || c.disputedAt || c.created_at || c.action_date;
                      const raisedOn = fmtDate(raisedOnDate, { includeTime: true });
                      const raisedBy = disputeItem?.userName || disputeItem?.username || disputeItem?.user_name || c.userName || c.disputedBy || '--';
                      const statusInfo = arStatusInfo(c, 'dispute');

                      return (
                        <TableRow
                          key={appId}
                          hover
                          sx={{
                            '&:last-child td': { borderBottom: 0 },
                            background: idx % 2 === 0 ? '#fff' : '#fafbfd',
                            '&:hover': { background: '#eef4ff !important' },
                          }}
                        >
                          <TableCell
                            className="ar-sticky-col-app-td"
                            sx={{
                              ...stickyAppTdSx,
                              fontWeight: 700,
                              color: '#3D7CC9',
                              fontSize: '13px',
                              py: 1.6,
                              px: 1.5,
                              background: idx % 2 === 0 ? '#fff' : '#fafbfd',
                            }}
                            title={appId}
                          >
                            {appId}
                          </TableCell>
                          <TableCell
                            className="ar-sticky-col-pt-td"
                            sx={{
                              ...stickyPtTdSx,
                              fontWeight: 500,
                              fontSize: '13px',
                              py: 1.6,
                              px: 1.5,
                              background: idx % 2 === 0 ? '#fff' : '#fafbfd',
                            }}
                            title={pt}
                          >
                            {pt}
                          </TableCell>
                          <TableCell
                            className="ar-disease-td"
                            sx={{
                              color: '#0d1f35',
                              fontSize: '13px',
                              py: 1.6,
                              pl: 3,
                              pr: 2,
                              minWidth: 175,
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {disease}
                          </TableCell>
                          <TableCell className="ar-date-cell" sx={{ color: '#7a84a3', fontSize: '13px', py: 1.8, px: 2, whiteSpace: 'nowrap' }}>{apd}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#0d1f35', py: 1.8, px: 2 }}>{totalRev != null ? inr(totalRev) : '--'}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#0d1f35', py: 1.8, px: 2, whiteSpace: 'nowrap' }}>{inr(c.collectedAtHospital != null ? c.collectedAtHospital : 0)}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '13px', color: '#0d1f35', py: 1.8, px: 2, whiteSpace: 'nowrap' }}>{inr(c.collectedAtGHV != null ? c.collectedAtGHV : 0)}</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '13px', color: '#5a12cc', py: 1.8, px: 2 }}>{inr(hs)}</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '13px', color: Number(gs) < 0 ? '#b75c00' : '#008a6e', py: 1.8, px: 2 }}>{inr(gs)}</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '13px', color: hospitalArapVal != null && Number(hospitalArapVal) < 0 ? '#b75c00' : '#008a6e', py: 1.8, px: 2 }}>
                            {hospitalArapVal != null ? inr(hospitalArapVal) : '--'}
                          </TableCell>
                          <TableCell sx={{ py: 1.8, px: 2 }}>
                            <span style={{ fontSize: '11px', fontWeight: 600, background: '#f4f0ff', color: '#6829ce', padding: '3px 8px', borderRadius: '6px', whiteSpace: 'nowrap' }}>
                              {disputeBucketVal}
                            </span>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200, fontSize: '12px', color: '#5d3a00', py: 1.8, px: 2 }}>
                            <Tooltip title={lastRemark} arrow placement="top">
                              <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {lastRemark}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell className="ar-date-cell" sx={{ color: '#7a84a3', fontSize: '12px', py: 1.8, px: 2, whiteSpace: 'nowrap' }}>{raisedOn}</TableCell>
                          <TableCell sx={{ color: '#0d1f35', fontWeight: 600, fontSize: '13px', py: 1.8, px: 2, whiteSpace: 'nowrap' }}>{raisedBy}</TableCell>
                          <TableCell sx={{ py: 1.8, px: 2 }}>
                            <span className={`ar-pill ${statusInfo.cls}`}>{statusInfo.label}</span>
                          </TableCell>
                          <TableCell sx={tdStickySx} className="ar-sticky-action-td" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => onView(appId, c)}
                              sx={{
                                textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                                fontWeight: 600, borderColor: '#3D7CC9', color: '#3D7CC9',
                                whiteSpace: 'nowrap', '&:hover': { background: '#eef4ff', borderColor: '#3D7CC9' },
                              }}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <PaginationBar page={page} totalPages={totalPages} onChange={setPage} />
          </div>
      )}
    </>
  );
};

// ─── Searchable Hospital Dropdown Component ─────────────────────────────────────
const SearchableHospitalSelector = ({
  hospitals = [],
  selectedHospitalId,
  onSelectHospital,
  loading = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm('');
  };

  const open = Boolean(anchorEl);

  // Find currently selected hospital object
  const selectedHosp = hospitals.find((h) => h.id === selectedHospitalId) || hospitals[0] || null;

  // Filter hospitals by Name OR ID
  const filteredHospitals = useMemo(() => {
    if (!searchTerm.trim()) return hospitals;
    const term = searchTerm.trim().toLowerCase();
    return hospitals.filter((h) => {
      const nameMatch = (h.name || '').toLowerCase().includes(term);
      const idMatch = (h.id || '').toLowerCase().includes(term);
      return nameMatch || idMatch;
    });
  }, [hospitals, searchTerm]);

  if (!hospitals.length && !loading) {
    return null;
  }

  return (
    <>
      <div
        className={`ar-hosp-btn${open ? ' open' : ''}`}
        onClick={handleClick}
        tabIndex={0}
        role="button"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <div className="ar-hosp-btn-icon">
          <ApartmentOutlined sx={{ fontSize: 18 }} />
        </div>
        <div
          className="ar-hosp-btn-text"
          title={selectedHosp ? `${selectedHosp.name} (${selectedHosp.id})` : 'Select Hospital'}
        >
          {selectedHosp ? (
            <>
              {selectedHosp.name} <small>({selectedHosp.id})</small>
            </>
          ) : (
            'Select Hospital'
          )}
        </div>
        <KeyboardArrowDownOutlined className="ar-hosp-btn-arrow" sx={{ fontSize: 18 }} />
      </div>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '12px',
            boxShadow: '0 12px 32px rgba(13, 31, 53, 0.16)',
            border: '1px solid #e3e8f3',
            overflow: 'hidden',
          },
        }}
      >
        <div className="ar-hosp-popover-card">
          {/* Search box */}
          <div className="ar-hosp-search-box">
            <TextField
              inputRef={searchInputRef}
              fullWidth
              autoFocus
              size="small"
              placeholder="Search hospital name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined sx={{ fontSize: 18, color: '#3D7CC9' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      sx={{ p: 0.2, color: '#7a84a3' }}
                    >
                      <CloseOutlined sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '12px',
                  background: '#f8fafc',
                  height: '36px',
                  '& fieldset': { borderColor: '#dde3ef' },
                  '&:hover fieldset': { borderColor: '#3D7CC9' },
                  '&.Mui-focused fieldset': { borderColor: '#3D7CC9', borderWidth: '1.5px' },
                },
              }}
            />
          </div>

          {/* Meta Count */}
          <div className="ar-hosp-meta-row">
            <span>AR Enabled Hospitals</span>
            <span>{filteredHospitals.length} of {hospitals.length}</span>
          </div>

          {/* Hospital List */}
          <div className="ar-hosp-list">
            {loading ? (
              <div className="ar-hosp-empty">
                <CircularProgress size={20} sx={{ color: '#3D7CC9', mb: 1, display: 'block', mx: 'auto' }} />
                Loading hospitals...
              </div>
            ) : filteredHospitals.length === 0 ? (
              <div className="ar-hosp-empty">
                <SearchOffOutlined sx={{ fontSize: 24, color: '#9aa3bc', display: 'block', mx: 'auto', mb: 0.5 }} />
                No hospitals found matching "{searchTerm}"
              </div>
            ) : (
              filteredHospitals.map((h) => {
                const isSelected = h.id === selectedHospitalId;
                return (
                  <div
                    key={h.id}
                    className={`ar-hosp-item${isSelected ? ' selected' : ''}`}
                    onClick={() => {
                      onSelectHospital(h.id);
                      handleClose();
                    }}
                  >
                    <div className="ar-hosp-item-info">
                      <div className="ar-hosp-item-name" title={`${h.name} (${h.id})`}>
                        {h.name}{' '}
                        <span style={{ color: isSelected ? '#1565c0' : '#7a84a3', fontWeight: 500, fontSize: '12px' }}>
                          ({h.id})
                        </span>
                      </div>
                    </div>
                    {isSelected && (
                      <CheckCircle sx={{ fontSize: 18, color: '#3D7CC9', ml: 1, flexShrink: 0 }} />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};

// ─── Root Component ────────────────────────────────────────────────────────────
const ARReconciliation = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const authUser = useSelector((state) => state.authentication.user);
  const hospitalsByIds = useSelector((state) => state.hospitals?.hospitalsByIds) || {};
  const hospitalList = useSelector((state) => state.hospitals?.hospitalList) || [];

  const canAccess = hasArAccess(authUser);
  const canAction = canPerformArAction(authUser);

  // Left sidebar collapse state with persistence
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('ar_sidebar_collapsed') === 'true';
    } catch (e) {
      return false;
    }
  });

  const handleToggleSidebar = (val) => {
    setIsSidebarCollapsed(val);
    try {
      localStorage.setItem('ar_sidebar_collapsed', String(val));
    } catch (e) {}
  };

  // Fetch AR Hospitals List from getArHospitalList API
  const [arHospitalList, setArHospitalList] = useState([]);
  const [isFetchingArHospitals, setIsFetchingArHospitals] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setIsFetchingArHospitals(true);
    getArHospitalListService()
      .then((res) => {
        if (!isMounted) return;
        const list =
          res?.data?.hospitals ||
          (Array.isArray(res?.data) ? res.data : (res?.hospitals || []));
        setArHospitalList(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error('Failed to fetch AR hospital list:', err);
      })
      .finally(() => {
        if (isMounted) setIsFetchingArHospitals(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Match AR hospitals against user's accessible hospitalList
  const matchedHospitals = useMemo(() => {
    // If specific hospitals are restricted for this user (e.g. mobile 8699116698)
    const restrictedHospitals = getUserRestrictedHospitals(authUser);
    if (restrictedHospitals && restrictedHospitals.length > 0) {
      return restrictedHospitals.map((h) => ({
        id: h.id,
        name: h.name,
        originalName: h.name,
      }));
    }

    if (!arHospitalList || arHospitalList.length === 0) return [];

    return arHospitalList
      .filter((arHosp) => {
        const id = arHosp?.id;
        if (!id) return false;
        return Boolean(hospitalsByIds[id] || hospitalList.some((h) => h.hospital_id === id));
      })
      .map((arHosp) => {
        const id = arHosp.id;
        const userHosp = hospitalsByIds[id] || hospitalList.find((h) => h.hospital_id === id);
        const displayName = userHosp?.friendly_name || userHosp?.hospital_name || arHosp.name || id;
        return {
          id,
          name: displayName,
          originalName: arHosp.name,
        };
      });
  }, [arHospitalList, hospitalsByIds, hospitalList, authUser]);

  const hospitalIds = useMemo(() => matchedHospitals.map((h) => h.id), [matchedHospitals]);

  const paramTab = searchParams.get('tab');
  const tab = (paramTab === 'signed' || paramTab === 'disp') ? paramTab : 'dash';
  const detailApp = searchParams.get('appId');

  const paramHospitalId = searchParams.get('hospitalId');
  const selectedHospitalId =
    (paramHospitalId && hospitalIds.includes(paramHospitalId))
      ? paramHospitalId
      : (hospitalIds[0] || '');

  const setSelectedHospitalId = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('hospitalId', id);
    next.delete('appId');
    setSearchParams(next);
  };

  useEffect(() => {
    if (!hospitalIds.length) return;
    if (!paramHospitalId || !hospitalIds.includes(paramHospitalId)) {
      const next = new URLSearchParams(searchParams);
      next.set('hospitalId', hospitalIds[0]);
      setSearchParams(next, { replace: true });
    }
  }, [hospitalIds, paramHospitalId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [detailSummary, setDetailSummary] = useState(null);

  // Lists state
  const [openCases, setOpenCases] = useState([]);
  const [signedCases, setSignedCases] = useState([]);
  const [disputeCases, setDisputeCases] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAllCases = useCallback(async () => {
    if (!location.pathname.includes('/ar-reconciliation')) {
      return;
    }
    if (!selectedHospitalId) {
      setOpenCases([]);
      setSignedCases([]);
      setDisputeCases([]);
      return;
    }

    setLoading(true);
    try {
      const [openRes, signedRes, disputeRes] = await Promise.allSettled([
        getArStatusDataService({ hospitalId: selectedHospitalId }),
        getArStatusDataService({ hospitalId: selectedHospitalId, hospitalStatus: 'hospital_approve' }),
        getArStatusDataService({ hospitalId: selectedHospitalId, hospitalStatus: 'hospital_dispute' }),
      ]);

      if (openRes.status === 'fulfilled' && openRes.value?.data) {
        setOpenCases(openRes.value.data);
      }

      if (signedRes.status === 'fulfilled' && signedRes.value?.data) {
        setSignedCases(signedRes.value.data);
      }

      if (disputeRes.status === 'fulfilled' && disputeRes.value?.data) {
        setDisputeCases(disputeRes.value.data);
      }
    } catch (err) {
      console.error('Error fetching AR data:', err);
      toast.error('Unable to fetch live AR data');
    } finally {
      setLoading(false);
    }
  }, [selectedHospitalId, location.pathname]);

  useEffect(() => {
    if (canAccess && location.pathname.includes('/ar-reconciliation')) {
      dispatch(homeStateActions.setActiveTab('ar-reconciliation'));
      fetchAllCases();
    }
  }, [location.pathname, fetchAllCases, dispatch, canAccess]);

  const handleTabChange = (newTab) => {
    const next = new URLSearchParams();
    if (selectedHospitalId) next.set('hospitalId', selectedHospitalId);
    if (newTab && newTab !== 'dash') {
      next.set('tab', newTab);
    }
    setSearchParams(next);
    setDetailSummary(null);
  };

  const handleViewDetail = (appId, summary) => {
    setDetailSummary(summary || null);
    const next = new URLSearchParams(searchParams);
    next.set('appId', appId);
    setSearchParams(next);
  };

  const handleBackFromDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('appId');
    setSearchParams(next);
    setDetailSummary(null);
  };

  const handleCaseActionSuccess = (appId, action) => {
    fetchAllCases();
    const next = new URLSearchParams();
    if (selectedHospitalId) next.set('hospitalId', selectedHospitalId);
    if (action === 'hospital_approve' || action === 'approve') {
      next.set('tab', 'signed');
    } else if (action === 'hospital_dispute' || action === 'dispute') {
      next.set('tab', 'disp');
    }
    setSearchParams(next);
    setDetailSummary(null);
  };

  // Sign Off or Raise Dispute action handler
  const handleCaseAction = async (caseObj, action, disputePayload = {}) => {
    if (!canAction) {
      toast.error('You do not have permission to perform this action');
      return;
    }
    const appId = caseObj?.appId || caseObj?.app;
    const hospitalId = caseObj?.hospitalId || selectedHospitalId;
    const isApprove = action === 'approve' || action === 'hospital_approve';
    const resolvedStatus = isApprove ? 'hospital_approve' : 'hospital_dispute';

    try {
      if (isApprove) {
        await updateArStatusService({
          appId,
          hospitalId,
          status: 'hospital_approve',
        });
        toast.success(`Case ${appId} signed off successfully!`);
      } else {
        await updateArStatusService({
          appId,
          hospitalId,
          status: 'hospital_dispute',
          remark: disputePayload.remark || 'billing mismatch',
          arap_at_hospital: disputePayload.arap_at_hospital,
          dispute_bucket: disputePayload.dispute_bucket,
          files: disputePayload.files || [],
        });
        toast.info(`Dispute submitted for ${appId}`);
      }
      // Instantly remove from pending open cases list
      setOpenCases((prev) => prev.filter((c) => (c.appId || c.app) !== appId));
      handleCaseActionSuccess(appId, resolvedStatus);
    } catch (err) {
      console.error('AR case action error:', err);
      toast.error(err?.message || 'Action failed, please try again');
      throw err;
    }
  };

  const activeSummary =
    detailSummary ||
    openCases.find((c) => (c.appId || c.aid) === detailApp) ||
    signedCases.find((c) => (c.appId || c.aid) === detailApp) ||
    disputeCases.find((c) => (c.appId || c.aid) === detailApp) ||
    null;

  if (!canAccess) {
    return <Navigate to="/home" replace />;
  }

  const hospitalSelector = (
    <SearchableHospitalSelector
      hospitals={matchedHospitals}
      selectedHospitalId={selectedHospitalId}
      onSelectHospital={(id) => {
        setSelectedHospitalId(id);
        setDetailSummary(null);
      }}
      loading={isFetchingArHospitals}
    />
  );

  return (
    <div className="ar-layout">
      <Sidebar
        tab={tab}
        setTab={handleTabChange}
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={handleToggleSidebar}
      />
      <div className="ar-main">
        {detailApp ? (
          <ARDetail
            appId={detailApp}
            caseSummary={activeSummary}
            fromTab={tab}
            onBack={handleBackFromDetail}
          />
        ) : isFetchingArHospitals && matchedHospitals.length === 0 ? (
          <div className="ar-panel">
            <div className="ar-loading-box">
              <CircularProgress size={32} sx={{ color: '#3D7CC9' }} />
              <span>Loading AR hospitals...</span>
            </div>
          </div>
        ) : matchedHospitals.length === 0 ? (
          <div className="ar-panel">
            <div className="ar-empty">No AR-enabled hospital found for your account.</div>
          </div>
        ) : (
          <>
            {tab === 'dash' && (
              <ARDashboard
                cases={openCases}
                loading={loading}
                onRefresh={fetchAllCases}
                onView={handleViewDetail}
                openCount={openCases.length}
                signedCount={signedCases.length}
                disputeCount={disputeCases.length}
                onTabSelect={handleTabChange}
                hospitalSelector={hospitalSelector}
                onCaseAction={handleCaseAction}
                canAction={canAction}
              />
            )}
            {tab === 'signed' && (
              <ARSignedOff
                cases={signedCases}
                loading={loading}
                onRefresh={fetchAllCases}
                onView={handleViewDetail}
                hospitalSelector={hospitalSelector}
              />
            )}
            {tab === 'disp' && (
              <ARDisputes
                cases={disputeCases}
                loading={loading}
                onRefresh={fetchAllCases}
                onView={handleViewDetail}
                hospitalSelector={hospitalSelector}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ARReconciliation;

